class ProductGrid extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.state = {
      root: window.theme.tagalys.results.stateLayer.state,
    }

    this.initialLoad = false

    // Store bound handlers for cleanup
    this._boundHandlers = {
      handleProductsUpdated: products => this.render(products),
      handleShopifyProductsFetched: () => this.fallbackRender(),
    }

    this.productCache = new Map()
    this.skippedCount = 0
  }

  connectedCallback () {
    this.subscribeToEvents()
  }

  disconnectedCallback () {
    this.unsubscribeFromEvents()
  }

  subscribeToEvents () {
    window.theme.tagalys.results.eventBus.subscribe(
      'results:state:products:updated',
      this._boundHandlers.handleProductsUpdated,
    )
    window.theme.tagalys.results.eventBus.subscribe(
      'results:shopify:products:fetched',
      this._boundHandlers.handleShopifyProductsFetched,
    )
  }

  unsubscribeFromEvents () {
    window.theme.tagalys.results.eventBus.unsubscribe(
      'results:state:products:updated',
      this._boundHandlers.handleProductsUpdated,
    )
    window.theme.tagalys.results.eventBus.unsubscribe(
      'results:shopify:products:fetched',
      this._boundHandlers.handleShopifyProductsFetched,
    )
  }

  _returnPromosToHiddenContainer () {
    const gridContainer = this.productGrid
    const preRenderedPromosContainer = document.getElementById(
      'pre-rendered-promos',
    )
    if (!gridContainer || !preRenderedPromosContainer) {
      return
    }
    const insertedPromoElements = gridContainer.querySelectorAll(
      '[data-inserted-promo="true"]',
    )
    insertedPromoElements.forEach(promoElement => {
      const originalPosition = promoElement.dataset.originalPosition
      if (!originalPosition) {
        return
      }
      const originalWrapper = preRenderedPromosContainer.querySelector(
        `[data-promo-position="${originalPosition}"]`,
      )
      if (originalWrapper) {
        delete promoElement.dataset.insertedPromo
        delete promoElement.dataset.originalPosition
        originalWrapper.appendChild(promoElement)
      }
    })
  }

  render (products) {
    const count = products.length
    const gridContainer = this.productGrid
    let renderPromos = true

    const requestType = window.theme.tagalys.results.SDK.helpers.getRequestParams()
      ?.action

    if (requestType === 'APPLY_FILTER') {
      renderPromos = false
    }

    if (!gridContainer) {
      return
    }

    if (document.querySelector('[data-product-grid-external-el="count"]')) {
      document.querySelector(
        '[data-product-grid-external-el="count"]',
      ).textContent = this.state.root.pagination.total - this.skippedCount
    }

    this._returnPromosToHiddenContainer()

    const potentialPromos = this._getPotentialPromosForCurrentPage()

    if (count === 0 && potentialPromos.length === 0) {
      gridContainer.innerHTML = ''

      gridContainer.insertAdjacentHTML(
        'beforeend',
        `
        <div class="no-results">
          <p>No results found</p>
        </div>
      `,
      )
      return
    }

    const newSortBy = this.state.root.sortOptions?.find(
      option => option.selected === true,
    )?.id

    if (
      this.initialLoad === false ||
      this.currentSortBy !== newSortBy ||
      this.currentFilters !== JSON.stringify(this.state.root.activeFacets || [])
    ) {
      gridContainer.innerHTML = ''
      this.skippedCount = 0
    }

    this.currentSortBy = newSortBy
    this.currentFilters = JSON.stringify(this.state.root.activeFacets || [])
    this.initialLoad = true

    this.renderProductSkeletons(products)
    // this.buildCards(products)
    this.intersectionObserver()

    if (renderPromos) {
      this._insertPromoCards()
    }
  }

  async fallbackRender () {
    this.productGrid.innerHTML = ''
    try {
      const url = `/collections/${this.collectionHandle}?section_id=${this.fallbackSectionId}`
      const fullUrl = this.pageParam ? `${url}&page=${this.pageParam}` : url
      const response = await fetch(fullUrl)

      if (!response.ok) {
        throw new Error('Failed to fetch fallback product grid section')
      }
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')
      const gridHTML = html.querySelector(
        `[data-product-grid-external-el="fallbackProductGrid"]`,
      ).innerHTML

      this.innerHTML = gridHTML
    } catch (error) {
      console.error('Failed to fetch swatches section:', error)
    }
  }

  /**
   * Renders skeleton loading placeholders for products.
   * @param {Array<Object>} products - Array of product objects
   */
  renderProductSkeletons (products) {
    const fragment = document.createDocumentFragment()

    products.forEach(product => {
      const li = document.createElement('li')

      if (
        window.location.hash &&
        window.location.hash.startsWith('#wishlist-')
      ) {
        li.setAttribute('id', `wishlist-${product.id}`)
      } else {
        li.setAttribute('id', `product-${product.id}`)
      }

      const url = `/products/${product.handle}`

      li.classList.add('prd-List_Item')
      li.setAttribute('data-product-grid-el', 'item')
      li.setAttribute('data-url', url)
      li.setAttribute('data-loading', 'true')
      li.innerHTML = this.createSkeletonHTML()

      fragment.appendChild(li)
    })

    if (!this.lastPage) {
      this.lastPage = this.state.root.pagination.page
    }

    if (this.lastPage > this.state.root.pagination.page) {
      this.productGrid.prepend(fragment)
    } else {
      this.productGrid.appendChild(fragment)
    }

    this.lastPage = this.state.root.pagination.page
  }

  /**
   * Builds and renders product cards for the first 8 products.
   * @param {Array<Object>} products - Array of product objects
   */
  buildCards (products) {
    const productsToRender = products.slice(0, 8)
    const items = this.els.item.elements

    if (products.length < items.length) {
      for (let i = products.length; i < items.length; i++) {
        items[i].style.display = 'none'
      }
    } else {
      items.forEach(item => {
        item.style.display = ''
      })
    }

    productsToRender.forEach((product, index) => {
      items[index].innerHTML = `
        <div class="prd-Card">
          <div class="prd-Card_MediaContainer">
            <img
              src="${product.featured_image}"
              alt="${product.title}"
              style="aspect-ratio: 650 / 840; object-fit: cover;"
            >
          </div>
          <div class="prd-Card_Body">
            <div class="prd-Card_Title fz-15_18">
              ${product.title}
            </div>
            <div class="prd-Card_Price fz-15_35" style="height: 25px;">
            </div>
          </div>
        </div>
      `
    })
  }

  /**
   * Sets up the Intersection Observer for lazy loading products.
   * Loads products when they come into view.
   */
  intersectionObserver () {
    const options = {
      root: null,
      rootMargin: '800px',
      threshold: 0,
    }

    this.observer = new IntersectionObserver(entries => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          if (entry.target.dataset.loading === 'true') {
            entry.target.dataset.loading = false

            this.fetchProductWithRetry(entry.target.dataset.url, index)
              .then(html => {
                if (!html || !html.trim()) {
                  entry.target.remove()
                  this.skippedCount++
                  const countEl = document.querySelector(
                    '[data-product-grid-external-el="count"]',
                  )
                  if (countEl) {
                    countEl.textContent =
                      this.state.root.pagination.total - this.skippedCount
                  }
                  return
                }
                entry.target.innerHTML = html
              })
              .catch(error => {
                console.error(
                  `Error fetching product ${entry.target.dataset.url}:`,
                  error,
                )
              })
          }
        }
      })
    }, options)

    this.els.item?.elements.forEach(item => {
      this.observer.observe(item)
    })
  }

  /**
   * Creates HTML for a product skeleton loading placeholder.
   * @param {Object} product - Product object containing at least an id
   * @returns {string} HTML string for the skeleton
   */
  createSkeletonHTML () {
    return `
      <div class="prd-CardSkeleton">
        <div class="prd-CardSkeleton_Image"></div>
        <div class="prd-CardSkeleton_Body"></div>
      </div>
    `
  }

  async fetchProduct (url, index) {
    if (!url) {
      console.error('No URL provided for product fetch')
      return null
    }

    const currentPath = window.location.pathname
    const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})\//)

    if (
      localeMatch &&
      localeMatch[1] &&
      !url.startsWith(`/${localeMatch[1]}`)
    ) {
      const localePrefix = `/${localeMatch[1]}`
      url = `${localePrefix}${url}`
    }

    if (this.productCache.has(url)) {
      return this.productCache.get(url)
    }

    const html = await window.BAO.sectionRenderer.fetchSingle(
      this.cardSectionId,
      url,
    )
    const innerHTML = window.BAO.sectionRenderer.getInnerHtml(html, {
      id: `shopify-section-${this.cardSectionId}`,
    })
    this.productCache.set(url, innerHTML)
    return innerHTML
  }

  async fetchProductWithRetry (url, index, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await this.fetchProduct(url, index)
      } catch (error) {
        if (i === retries - 1) throw error
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000))
      }
    }
  }

  get productGrid () {
    return this.querySelector('.vbf-product-grid')
  }

  get cardSectionId () {
    return this.dataset.cardSection || 'product-card'
  }

  get templateProductCard () {
    return document.getElementById('templateProductCard')
  }

  get templateHeroCard () {
    return document.getElementById('templateHeroCard')
  }

  get fallbackSectionId () {
    return 'fallback-product-grid'
  }

  get collectionHandle () {
    return this.getAttribute('data-collection-handle')
  }

  get pageParam () {
    return new URLSearchParams(window.location.search).get('page')
  }

  get allPromosData () {
    if (!this._allPromosData) {
      const promoDataEl = document.querySelector('[data-promos]')
      try {
        this._allPromosData = promoDataEl
          ? JSON.parse(
            promoDataEl.textContent.trim().replace(/,(\s*[\]}])/g, '$1'),
          )
          : []
      } catch (e) {
        console.error('Failed to parse promo data', e)
        this._allPromosData = []
      }
    }
    return this._allPromosData
  }

  _getPotentialPromosForCurrentPage () {
    const currentPage = this.state.root.pagination.page || 1
    const itemsPerPage = window.theme.resultsConfig.productsPerPage || 40
    const firstItemIndexOnPage = (currentPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = currentPage * itemsPerPage
    const allData = this.allPromosData || []
    return allData.filter(
      p =>
        p.position >= firstItemIndexOnPage && p.position <= lastItemIndexOnPage,
    )
  }

  _insertPromoCards () {
    const activeFacets = this.state.root.activeFacets || []
    if (activeFacets.length > 0) {
      return
    }

    if (
      this.lowestLoadedPage === undefined ||
      this.state.root.pagination.page < this.lowestLoadedPage
    ) {
      this.lowestLoadedPage = this.state.root.pagination.page
    }

    if (
      this.highestLoadedPage === undefined ||
      this.state.root.pagination.page > this.highestLoadedPage
    ) {
      this.highestLoadedPage = this.state.root.pagination.page
    }

    const itemsPerPage = window.theme.resultsConfig.productsPerPage || 48
    const gridContainer = this.productGrid
    const preRenderedPromosContainer = document.getElementById(
      'pre-rendered-promos',
    )

    if (!gridContainer || !preRenderedPromosContainer) {
      return
    }

    const firstItemIndexOnPage = (this.lowestLoadedPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = this.highestLoadedPage * itemsPerPage

    const promoWrappersToShow = Array.from(preRenderedPromosContainer.children)
      .filter(promoWrapper => {
        if (
          !(promoWrapper instanceof HTMLElement) ||
          !promoWrapper.dataset ||
          !promoWrapper.dataset.promoPosition
        ) {
          return false
        }
        const position = parseInt(promoWrapper.dataset.promoPosition, 10)
        return (
          !isNaN(position) &&
          position >= firstItemIndexOnPage &&
          position <= lastItemIndexOnPage
        )
      })
      .sort(
        (a, b) =>
          parseInt(a.dataset.promoPosition, 10) -
          parseInt(b.dataset.promoPosition, 10),
      )

    promoWrappersToShow.forEach(promoWrapper => {
      const position = parseInt(promoWrapper.dataset.promoPosition, 10)
      const promoElement = promoWrapper.firstElementChild

      if (!promoElement) {
        return
      }

      promoElement.dataset.insertedPromo = 'true'
      promoElement.dataset.originalPosition = position

      const targetPageIndex = position - firstItemIndexOnPage
      const elementAtIndex = gridContainer.children[targetPageIndex]

      if (elementAtIndex) {
        gridContainer.insertBefore(promoElement, elementAtIndex)
      } else {
        gridContainer.appendChild(promoElement)
      }
    })
  }
}

customElements.define('product-grid', ProductGrid)
